import fileDownload from 'js-file-download';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumb, Card, Col, Dropdown, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppContext } from '../../../common';
import { DeleteWorkshiftModal, WorkshiftSchedulingModal } from '../../../common/components/modals';
import { Private_Routes } from '../../../constants';
import { getEmployeesIds } from '../../../utils';
import { KpisContainer } from '../components/kpis/KpisContainer';
import { OverTimeScheduleTable } from '../components/table/OverTimeScheduleTable';
import { OverTimeScheduleToolsPagination } from '../components/table/OverTimeScheduleToolsPagination';
import { ErrorCard } from '../components/ui-tools/ErrorCard';
import { KpisContainerSkeleton } from '../components/ui-tools/KpisContainerSkeleton';
import { OvertimeScheduleTableSkeleton } from '../components/ui-tools/OvertimeScheduleTableSkeleton';
import { useDownloadOvertimeReport } from '../hooks/useOvertimeReports';
import {
  useDeleteWorkshiftSchedulingMutation,
  useOvertimeSchedule,
  useSaveWorkshiftSchedulingMutation,
} from '../hooks/useOvertimeSchedule';
import { useValidateOvertimeSchedule } from '../hooks/useValidateOvertimeSchedule';
import { OvertimeWorkedAndScheduledPerPeriodRequest } from '../models/OvertimeSchedule';
import { buildPeriodsFromData, formatPeriodLabel, getPeriodDatesFromData } from '../utils/period.utils';
import { useDownloadConsolidateByHourTypeOvertimeReport } from '../hooks/useConsolidateByHourTypeOvertimeReports';
import {
  markOvertimeHomeRestorePending,
  OvertimeHomeSnapshot,
  saveOvertimeHomeSnapshot,
} from '../utils/overtimeHomeStatePersistence';

export const OvertimeScheduleIndexPage = () => {
  const navigate = useNavigate();

  const {
    isOpenWorkshiftModal,
    setIsOpenWorkshiftModal,
    setIsOpenDeleteWorkshiftModal,
    isOpenDeleteWorkshiftModal,
    langResources,
    modalInfoWorkshift,
    selectedEmployees,
    employeesToShowTable,
    employeesSelectedBackup,
    showEmployeesTable,
    startDateScheduling,
    endDateScheduling,
    isCollapseTree,
  } = useContext(AppContext);

  const { employeesList } = useValidateOvertimeSchedule();

  const { mutate: downloadReport } = useDownloadOvertimeReport();
  const { mutate: downloadConsolidateByHourTypeReport } = useDownloadConsolidateByHourTypeOvertimeReport();

  const { mutate: saveWorkshiftScheduling } = useSaveWorkshiftSchedulingMutation();

  const { mutate: deleteWorkshiftScheduling } = useDeleteWorkshiftSchedulingMutation();

  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const buildHomeSnapshot = useCallback((): OvertimeHomeSnapshot => {
    return {
      selectedEmployees,
      employeesToShowTable,
      employeesSelectedBackup,
      startDateScheduling,
      endDateScheduling,
      showEmployeesTable,
      isCollapseTree,
      selectedNodeIds: selectedEmployees.map((e) => e.id),
    };
  }, [
    selectedEmployees,
    employeesToShowTable,
    employeesSelectedBackup,
    startDateScheduling,
    endDateScheduling,
    showEmployeesTable,
    isCollapseTree,
  ]);

  useEffect(() => {
    saveOvertimeHomeSnapshot(buildHomeSnapshot());
  }, []);

  const handleBackHome = () => {
    saveOvertimeHomeSnapshot(buildHomeSnapshot());
    markOvertimeHomeRestorePending();
    navigate(`/${Private_Routes.HOME}`);
  };

  const request: OvertimeWorkedAndScheduledPerPeriodRequest = useMemo(
    () => ({
      employeeIds: employeesList,
      startDate: startDateScheduling,
      endDate: endDateScheduling,
      preliquidateTypeId: null,
    }),
    [employeesList, startDateScheduling, endDateScheduling],
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useOvertimeSchedule(request);

  useEffect(() => {
    if (employeesList.length > 0) {
      refetch();
    }
  }, []);

  const showInitialSkeleton = isLoading;
  const isRefreshing = isFetching && !isLoading;

  const overtimeData = data || [];

  const periods = useMemo(() => buildPeriodsFromData(overtimeData), [overtimeData]);

  useEffect(() => {
    if (!periods.length) {
      if (selectedPeriodIndex !== 0) setSelectedPeriodIndex(0);
      return;
    }

    if (selectedPeriodIndex >= periods.length) {
      setSelectedPeriodIndex(periods.length - 1);
    }
  }, [periods, selectedPeriodIndex]);

  const selectedPeriod = periods[selectedPeriodIndex];

  const periodDates = useMemo(
    () => getPeriodDatesFromData(overtimeData, selectedPeriodIndex),
    [overtimeData, selectedPeriodIndex],
  );

  const selectedWeekLabel = useMemo(() => {
    if (!selectedPeriod) return 'Sin periodo';

    return formatPeriodLabel(selectedPeriod);
  }, [selectedPeriod]);

  // Search and filter logic with debounce
  const performSearch = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredData(overtimeData);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    const filtered = overtimeData.filter((employee) => {
      const fullName = `${employee.fullName || ''}`.toLowerCase();
      const documentNumber = (employee.documentNumber || '').toLowerCase();

      return fullName.includes(lowerSearchTerm) || documentNumber.includes(lowerSearchTerm);
    });

    setFilteredData(filtered);
  }, [searchTerm, overtimeData]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch();
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, performSearch]);

  // Initialize filteredData when overtimeData changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(overtimeData);
    }
  }, [overtimeData]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setFilteredData(overtimeData);
  }, [overtimeData]);

  const saveWorkshiftsScheduling = (workshiftId: number) => {
    const body = {
      workshiftId: workshiftId,
      startDate: modalInfoWorkshift.startDate ? `${modalInfoWorkshift.startDate}T00:00:00` : startDateScheduling,
      endDate: modalInfoWorkshift.endDate ? `${modalInfoWorkshift.endDate}T00:00:00` : endDateScheduling,
      employeeIds: getEmployeesIds(modalInfoWorkshift?.employee),
    };

    if (body.workshiftId) {
      const id = toast.loading(langResources['save.workshift.scheduling']['loading']);

      saveWorkshiftScheduling(body, {
        onSuccess: (data) => {
          if (data.daysResponse?.data?.length > 0) {
            toast.update(id, {
              render: `${langResources['save.workshift.scheduling']['error']}
              ${data.daysResponse.data.map((day) => day.name)}`,
              type: 'warning',
              isLoading: false,
              autoClose: 5000,
              position: toast.POSITION.BOTTOM_RIGHT,
              closeOnClick: true,
              closeButton: true,
            });
          } else {
            toast.update(id, {
              render: langResources['save.workshift.scheduling']['success'],
              type: 'success',
              isLoading: false,
              autoClose: 5000,
              position: toast.POSITION.BOTTOM_RIGHT,
              closeOnClick: true,
              closeButton: true,
            });

            setIsOpenWorkshiftModal(false);
          }
        },
        onError: () => {
          toast.update(id, {
            render: langResources['save.workshift.scheduling']['failed'],
            type: 'error',
            isLoading: false,
            autoClose: 5000,
            position: toast.POSITION.BOTTOM_RIGHT,
            closeOnClick: true,
            closeButton: true,
          });
        },
      });
    }
  };

  const deletedWorkshiftScheduling = () => {
    const id = toast.loading(langResources['delete.workshift.scheduling']['loading']);

    let body = {
      workshiftSchedulingIds: modalInfoWorkshift.id,
    };

    deleteWorkshiftScheduling(body, {
      onSuccess: () => {
        toast.update(id, {
          render: langResources['delete.workshift.scheduling']['success'],
          type: 'success',
          isLoading: false,
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_RIGHT,
          closeOnClick: true,
          closeButton: true,
        });
      },
      onError: () => {
        toast.update(id, {
          render: langResources['delete.workshift.scheduling']['error'],
          type: 'error',
          isLoading: false,
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_RIGHT,
          closeOnClick: true,
          closeButton: true,
        });
      },
    });

    setIsOpenDeleteWorkshiftModal(false);
  };

  const handlerDownloadReport = () => {
    const id = toast.loading('Descargando reporte...');

    downloadReport(request, {
      onSuccess: (res: any) => {
        fileDownload(res, `gestion_tiempos_extra-${new Date().toLocaleDateString()}.xlsx`);

        toast.update(id, {
          render: 'Reporte descargado correctamente',
          type: 'success',
          isLoading: false,
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_RIGHT,
          closeOnClick: true,
          closeButton: true,
        });
      },
      onError: () => {
        toast.update(id, {
          render: 'Ocurrio un error al descargar el reporte',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_RIGHT,
          closeOnClick: true,
          closeButton: true,
        });
      },
    });
  };

  const handlerDownloadConsolidateByHourTypeReport = () => {
    const id = toast.loading('Descargando reporte...');

    downloadConsolidateByHourTypeReport(request, {
      onSuccess: (res: any) => {
        fileDownload(res, `consolidado_gestion_tiempos_extra-${new Date().toLocaleDateString()}.xlsx`);

        toast.update(id, {
          render: 'Reporte descargado correctamente',
          type: 'success',
          isLoading: false,
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_RIGHT,
          closeOnClick: true,
          closeButton: true,
        });
      },
      onError: () => {
        toast.update(id, {
          render: 'Ocurrio un error al descargar el reporte',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_RIGHT,
          closeOnClick: true,
          closeButton: true,
        });
      },
    });
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <Row className="py-0">
            <Col sm={12} className="p-3 py-0">
              <Breadcrumb>
                <Breadcrumb.Item
                  onClick={isRefreshing ? undefined : handleBackHome}
                  role="button"
                  className={isRefreshing ? 'text-muted pe-none' : ''}
                >
                  Inicio
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Gestión de horas extra</Breadcrumb.Item>
              </Breadcrumb>
            </Col>
          </Row>

          {showInitialSkeleton ? (
            <>
              <KpisContainerSkeleton />
              <Card>
                <Card.Body>
                  <OvertimeScheduleTableSkeleton />
                </Card.Body>
              </Card>
            </>
          ) : isError && !data ? (
            <ErrorCard message={error instanceof Error ? error.message : undefined} onBack={handleBackHome} />
          ) : (
            overtimeData && (
              <>
                <KpisContainer data={overtimeData} selectedPeriodIndex={selectedPeriodIndex} />

                <Card>
                  <Card.Body>
                    <div
                      className="text-muted d-flex align-items-start justify-content-between mb-3 flex-wrap"
                      style={{ gap: 8 }}
                    >
                      <div>
                        <div className="mb-2 d-flex align-items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={handleBackHome}
                            disabled={isRefreshing}
                            className="btn btn-light btn-sm"
                          >
                            <i className="ri-arrow-left-line me-1" />
                            Volver
                          </button>
                          <button
                            type="button"
                            onClick={() => refetch()}
                            disabled={isRefreshing}
                            className="btn btn-light btn-sm"
                          >
                            <i className={`ri-refresh-line me-1 ${isRefreshing ? 'overtime-refresh-spin' : ''}`} />
                            {isRefreshing ? 'Actualizando...' : 'Actualizar'}
                          </button>
                          <div>
                            Del <strong>{startDateScheduling || '--'}</strong> al{' '}
                            <strong>{endDateScheduling || '--'}</strong>
                          </div>
                        </div>
                        Total de empleados: {overtimeData.length}
                      </div>

                      <div className="d-flex flex-column align-items-end gap-1">
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            variant="light"
                            size="sm"
                            className="d-flex gap-1 align-items-center"
                            disabled={isRefreshing}
                          >
                            <i className="mdi mdi-file-chart-outline" />
                            Reportes
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={handlerDownloadReport}>
                              <i className="mdi mdi-file-excel me-2" />
                              Gestión de tiempos extra
                            </Dropdown.Item>
                            {/*
                            Se comento esta opción porque esta en desarrollo el reporte
                            <Dropdown.Item
                              onClick={
                                handlerDownloadConsolidateByHourTypeReport
                              }
                            >
                              <i className="mdi mdi-file-excel me-2" />
                              Consolidado Gestión de tiempos extra
                            </Dropdown.Item> */}
                          </Dropdown.Menu>
                        </Dropdown>
                        <div>
                          Periodo seleccionado: <strong>{selectedWeekLabel}</strong>
                        </div>
                      </div>
                    </div>

                    <OverTimeScheduleToolsPagination
                      searchTerm={searchTerm}
                      onSearchChange={setSearchTerm}
                      onClear={handleClearSearch}
                      periods={periods}
                      totalEmployees={overtimeData.length}
                      selectedPeriodIndex={selectedPeriodIndex}
                      setSelectedPeriodIndex={setSelectedPeriodIndex}
                      disabled={isRefreshing}
                    />

                    <OverTimeScheduleTable
                      data={filteredData}
                      periodDates={periodDates}
                      selectedPeriodIndex={selectedPeriodIndex}
                      disabled={isRefreshing}
                    />
                  </Card.Body>
                </Card>

                {isOpenWorkshiftModal && (
                  <WorkshiftSchedulingModal
                    isOpen={isOpenWorkshiftModal}
                    saveWorkshiftsScheduling={saveWorkshiftsScheduling}
                  />
                )}

                {isOpenDeleteWorkshiftModal && (
                  <DeleteWorkshiftModal
                    isOpen={isOpenDeleteWorkshiftModal}
                    startDate={modalInfoWorkshift?.startDate}
                    endDate={modalInfoWorkshift?.endDate}
                    employee={modalInfoWorkshift?.employee}
                    setModalOpen={setIsOpenDeleteWorkshiftModal}
                    deleteWorkshiftsScheduling={deletedWorkshiftScheduling}
                  />
                )}
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};
